import { createSelector, createSlice } from "@reduxjs/toolkit";
import { generateClient, isCancelError } from 'aws-amplify/api';
import { crewMemberByLocationId, getCrewMember, searchCrewMembers } from "../graphql/queries";
import { reset } from "./globalActions";
import { updateCrewMember } from "../graphql/mutations";

const client = generateClient();

const moduleName = "crewMember";

const initialState = {
    loading: false,
    items: [],
    nextToken: null,
};

const crewMemberSlice = createSlice({
    name: moduleName,
    initialState,
    extraReducers: (builder) => {
        builder.addCase(reset, () => initialState);
    },
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setItems: (state, action) => {
            state.items = action.payload;
        },
        updateItem: (state, action) => {
            const idx = state.items.findIndex(i => i.id === action.payload.id);
            if (idx < 0) { state.items.push(action.payload) }
            else { state.items[idx] = action.payload }
        }
    },
});

export const { setLoading, setItems, updateItem } = crewMemberSlice.actions;
export default crewMemberSlice.reducer;

export const selectState = (state) => state[moduleName];
export const selectLoading = createSelector(selectState, (state) => state.loading);
export const selectCrewMembers = createSelector(selectState, (state) => state.items);
export const selectCrewMemberById = createSelector(
    selectCrewMembers,
    (state, id) => id,
    (crewMembers, id) => crewMembers.find((c) => c.id === id)
);

export const fetchCrewMembers = (locationId) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: crewMemberByLocationId,
            variables: {
                locationId,
                filter: {
                    active: { eq: true }
                },
                // limit: 999,
            },
        });
        dispatch(setItems(data.crewMemberByLocationId.items));
        return data.crewMemberByLocationId.items;
    } finally {
        dispatch(setLoading(false));
    }
};

let findCrewMembersPromise;
export const findCrewMembers = (search, locationId) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        if (findCrewMembersPromise) client.cancel(findCrewMembersPromise, 'Cancelled');
        findCrewMembersPromise = client.graphql({
            query: searchCrewMembers,
            variables: {
                filter: {
                    active: { eq: true },
                    locationId: { eq: locationId },
                    name: {
                        wildcard: `*${search}*`,
                    },
                },
                sort: { direction: "asc", field: "name" },
                limit: 300
            },
        });
        const { data: { searchCrewMembers: results } } = await findCrewMembersPromise
        dispatch(setItems(results.items));
        return results.items;
    } catch (ex) {
        if (!isCancelError(ex)) throw ex;
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchCrewMember = (id, locationId) => async (dispatch) => {
    const { data } = await client.graphql({
        query: getCrewMember,
        variables: {
            id,
            locationId,
        }
    });
    dispatch(updateItem(data.getCrewMember))
    return data.getCrewMember;
}

export const modifyCrewMember = ({ id, locationId, name, rate, email, active }) => async (dispatch) => {
    const { data } = await client.graphql({
        query: updateCrewMember,
        variables: {
            input: {
                id,
                active,
                locationId,
                name,
                rate,
                email
            }
        }
    });
    dispatch(updateItem(data.updateCrewMember))
    return data.updateCrewMember;
}
